



























import Vue, { PropType } from 'vue';
import PDateField from '../p-date-field/p-date-field.vue';
import PDateTimeField from '../p-date-time-field/p-date-time-field.vue';
// import PFileDrop from '../p-file-drop/p-file-drop.vue';
import PSelect from '../p-select/p-select.vue';
import PTextarea from '../p-textarea/p-textarea.vue';
import PTextField from '../p-text-field/p-text-field.vue';
import PCheckbox from '../p-checkbox/p-checkbox.vue';
import PCheckboxGroup from '../p-checkbox-group/p-checkbox-group.vue';
import PPhoneField from '../p-phone-field/p-phone-field.vue';
import PFieldset from '../p-fieldset/p-fieldset.vue';
import PAccordion from '../p-accordion/p-accordion.vue';
import DynamicFormFieldViewModel from '../../services/viewModel/resource/DynamicFormFieldViewModel';
import _ from '../../utils';
import { IJSONSchema } from '../../plugins/validation/IJSONSchema';
import { RequestSummaryItemResourceViewModel } from '../../services/viewModel/resource/RequestSummaryResourceViewModel';

/* eslint-disable no-unused-vars */
// eslint-disable-next-line no-shadow
export enum FormFieldInputTypeResource {
  TextSingleLine=0, // A single line of text
  TextMultiLine=1, // A multiline Text
  TextEmail=2, // A text containing an email-Address
  TextPhone=3, // A text containing a phone
  Date=4, // A date value
  DateTime=5, // A datetime value
  Decision=6, // boolean
  SelectSingle=7, // RadioButtons/Dropdown accepting only one answer
  SelectMultiple=8, // Checkbox/Listbox acception multiple answers
  File=9, // File upload
  Group=10 // Group of fields
}

interface IFormFieldItemResource {
  id: string,
  caption: string,
  order: number,
}

interface IFormFieldResource extends IFormFieldItemResource {
  items?: IFormFieldResource[] | null,
  isRequired: boolean,
  inputType: FormFieldInputTypeResource,
  picklistItems: IFormFieldItemResource[],
  defaultValue: string,
}

interface IFormFieldValueRequest {
  id: string;
  values: string[];
}

export default Vue.extend({
  name: 'PDynamicFormFields',
  props: {
    value: { type: Array, default: undefined },
    definition: { type: [Array, String] as PropType<IFormFieldResource[] | string>, default: () => [] },
    depth: { type: Number, default: 0 },
    groupsAreAccordions: { type: Boolean, default: false },
    jsonSchema: { type: Object, default: undefined },
    noValidation: { type: Boolean, default: false },
  },
  data: () => ({
    errorMessages: {} as Record<string, string>,
    output: [] as IFormFieldValueRequest[],
    userValues: {} as Record<string, string[] | string | number | boolean | File[] | undefined>,
    formFields: [] as {component: any, formField: IFormFieldResource}[],
  }),
  computed: {
    classList(): Record<string, boolean> {
      return {
        'p-dynamic-form-fields': this.depth === 0,
        'p-dynamic-form-fields-group': this.depth > 0,
        [`ml-${Math.min(this.depth * 2, 6)}`]: this.depth > 0 && this.groupsAreAccordions,
        'my-2': true,
      };
    },
  },
  watch: {
    userValues: {
      deep: true,
      handler() {
        this.output = this.convertUserValuesToOutput(this.userValues);
        this.$emit('input', this.output);
        this.emitComputed();
      },
    },
    groupsAreAccordions() {
      this.formFields = this.buildFormFieldsFromDefinition(this.definition);
    },
    definition: {
      immediate: true,
      deep: true,
      handler() {
        this.userValues = {};
        this.formFields = this.buildFormFieldsFromDefinition(this.definition);
        this.emitComputed();
      },
    },
  },
  methods: {
    validateComponent(formField: IFormFieldResource) {
      this.$nextTick(() => {
        this.$set(this.errorMessages, formField.id, undefined);
        const userValue = this.userValues[formField.id];
        if (formField.isRequired && !userValue) {
          this.$set(this.errorMessages, formField.id, this.$t('core.validation.required'));
        }
      });
    },
    // eslint-disable-next-line no-undef
    createJsonSchema(definition: IFormFieldResource[]): IJSONSchema {
      const schema = {
        type: 'array',
        allOf: [
          // for each field -> "contains: {}"
        ],
      } as IJSONSchema;
      definition.forEach((field) => {
        if (field.inputType === FormFieldInputTypeResource.Group) {
          const nestedSchema = this.createJsonSchema(field.items ?? []);
          schema.allOf = schema!.allOf!.concat(nestedSchema.allOf!);
        } else if (field.isRequired) {
          schema.allOf?.push({
            contains: {
              title: field.caption,
              properties: {
                id: { enum: [field.id] },
                values: {
                  contains: field.inputType === FormFieldInputTypeResource.Decision ? { enum: ['true'] } : undefined,
                  type: 'array',
                  minItems: 1,
                },
              },
            },
          });
        }
      });
      return schema;
    },
    getDisplayValue(definition: IFormFieldResource) {
      const userValue = this.userValues[definition.id];
      if (_.isNil(userValue)) {
        return undefined;
      }
      let displayValue = userValue.toString();
      if (!_.isEmpty(definition.picklistItems)) {
        const userValues = _.isArray(userValue) ? userValue : [userValue];
        const displayValues = [] as string[];
        userValues.forEach((val) => {
          const chosenPickItem = definition.picklistItems.find((pickItem) => pickItem.id === val);
          if (_.isSet(chosenPickItem)) {
            displayValues.push(chosenPickItem.caption);
          }
        });
        displayValue = displayValues.join(', ');
      }
      return displayValue;
    },
    createSummary(definition: IFormFieldResource[]) {
      const summaryItems = [] as RequestSummaryItemResourceViewModel[];
      definition.forEach((fieldDefinition) => {
        if (fieldDefinition.inputType !== FormFieldInputTypeResource.Group) {
          const displayValue = this.getDisplayValue(fieldDefinition);
          summaryItems.push(new RequestSummaryItemResourceViewModel().fromDTO({
            label: fieldDefinition.caption,
            displayValue,
          }));
        }
      });
      return summaryItems.filter((item) => _.isSet(item.displayValue));
    },
    emitComputed() {
      const definitionObj = this.parseDefinitionProp(this.definition);
      const schema = this.createJsonSchema(definitionObj);
      this.$emit('update:json-schema', schema);
      this.$emit('update:jsonSchema', schema);
      const summary = this.createSummary(definitionObj);
      this.$emit('update:summary', summary);
    },
    // eslint-disable-next-line no-undef
    convertUserValuesToOutput(userValues: typeof this.userValues) {
      const computedOutput = [] as IFormFieldValueRequest[];
      Object.keys(userValues).forEach((id) => {
        const formField = this.formFields.find((field) => field.formField.id === id);
        const inputType = formField?.formField.inputType;
        if (inputType === FormFieldInputTypeResource.Group) {
          // Skip groups in output since they aren't inputs
          return; // continue
        }
        const value = userValues[id];
        const unfilteredValues = _.isArray(value) ? value.map((entry) => entry?.toString()) : [value?.toString()];
        const values = unfilteredValues.filter((entry) => entry !== undefined && entry !== null) as string[];
        computedOutput.push(new DynamicFormFieldViewModel().fromDTO({
          id,
          values,
        }));
      });
      return computedOutput;
    },
    onGroupSchemaUpdate(schema: IJSONSchema) {

    },
    onGroupInput(values: IFormFieldValueRequest[]) {
      values.forEach((field) => {
        this.$set(this.userValues, field.id, field.values);
      });
    },
    mapToComponent(inputType: FormFieldInputTypeResource) {
      switch (inputType) {
      case FormFieldInputTypeResource.Date:
        return PDateField;
      case FormFieldInputTypeResource.DateTime:
        return PDateTimeField;
      case FormFieldInputTypeResource.Decision:
        return PCheckbox;
      case FormFieldInputTypeResource.File:
        // return PFileDrop;
        // TODO: No concept on how to send file data
        // Add once it is clearer how to handle such input
        throw new Error('\'File\' not implemented as dynamic form field type');
      case FormFieldInputTypeResource.SelectSingle:
        return PSelect;
      case FormFieldInputTypeResource.SelectMultiple:
        return PCheckboxGroup;
      case FormFieldInputTypeResource.TextEmail:
        return PTextField;
      case FormFieldInputTypeResource.TextMultiLine:
        return PTextarea;
      case FormFieldInputTypeResource.TextPhone:
        return PPhoneField;
      case FormFieldInputTypeResource.TextSingleLine:
        return PTextField;
      case FormFieldInputTypeResource.Group:
        return this.groupsAreAccordions ? PAccordion : PFieldset;
      default:
        throw new Error(`Unknown form field type ${inputType}`);
      }
    },
    // validateInput(id: string) {
    //   if (this.noValidation) {
    //     return;
    //   }
    //   const field = this.formFields.find((entry) => entry.formField.id === id);
    //   const isRequired = field?.formField.isRequired;
    //   const inputType = field?.formField.inputType;
    //   const value = this.userValues[id];
    //   if (isRequired && _.isEmpty(value)) {
    //     this.$set(this.errorMessages, id, this.$t('core.validation.required'));
    //     return;
    //   }
    //   switch (inputType) {
    //   case FormFieldInputTypeResource.Date:
    //   case FormFieldInputTypeResource.DateTime: {
    //     const isValidIsoDate = (value as string).match(validationPatterns.isoDateTime);
    //     if (!isValidIsoDate && value !== undefined) {
    //       this.$set(this.errorMessages, id, this.$t('core.validation.date'));
    //     }
    //     break;
    //   }
    //   default:
    //     break;
    //   }
    // },
    getIsValid() {
      let isValid = true;
      this.formFields.forEach((field) => {
        const { id } = field.formField;
        const { isRequired } = field.formField;
        const value = this.userValues[id];
        if (isRequired && (_.isEmpty(value))) {
          isValid = false;
        }
      });
      return isValid;
    },
    parseDefinitionString(definitionString: string) {
      return JSON.parse(definitionString) as IFormFieldResource[];
    },
    // eslint-disable-next-line no-undef
    parseDefinitionProp(definition: typeof this.definition) {
      let definitionObj = definition as IFormFieldResource[];
      if (typeof definition === 'string') {
        definitionObj = this.parseDefinitionString(definition);
      }
      return definitionObj;
    },
    buildFormFieldsFromDefinition(definition: string | IFormFieldResource[]) {
      let definitionObj = this.parseDefinitionProp(this.definition);
      definitionObj = _.sort(definitionObj, ((a, b) => a.order - b.order));
      const formFields = definitionObj.map((formField) => ({
        formField: { ...formField, picklistItems: _.sort(formField.picklistItems ?? [], (a, b) => a.order - b.order) },
        component: this.mapToComponent(formField.inputType),
      }));
      return formFields;
    },
  },
});
